import { ActionIcon, Center, Group, Popover, useComputedColorScheme } from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import { useLocation, type useMatchRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { SYSTEM_COLORS } from "@/configs/colorScheme";

import type { NavigationGroup } from "@/components/Navbar/useNavigationElements";

import styles from "./NavbarIconListItem.module.css";

type MatchRoute = ReturnType<typeof useMatchRoute>;

interface NavbarIconGroupItemProps {
  group: NavigationGroup;
  matchRoute: MatchRoute;
  children: ({ close }: { close: () => void }) => ReactNode;
}

export const NavbarIconGroupItem = ({ group, matchRoute, children }: NavbarIconGroupItemProps) => {
  const colorScheme = useComputedColorScheme();
  const isDarkTheme = colorScheme === "dark";

  const [opened, setOpened] = useState(false);
  const togglePopover = () => setOpened((prev) => !prev);
  const closePopover = () => setOpened(false);

  const ref = useClickOutside(() => setOpened(false));

  // Recompute active state inside a location selector so the group re-evaluates on client
  // navigation, not only on mount/reload — matchRoute alone does not trigger a re-render.
  const isActive = useLocation({
    select: () => group.items.some((item) => Boolean(matchRoute({ ...item.to, fuzzy: true }))),
  });

  return (
    <Popover position="right" withArrow shadow="md" opened={opened}>
      <Popover.Target>
        <Center>
          <ActionIcon
            autoContrast
            size="lg"
            onClick={togglePopover}
            color={isActive ? SYSTEM_COLORS.secondary : undefined}
            variant={isActive ? (isDarkTheme ? "light" : "filled") : "subtle"}
            classNames={{
              root: styles["icon-nav-link"],
            }}
          >
            {group.icon}
          </ActionIcon>
        </Center>
      </Popover.Target>
      <Popover.Dropdown>
        <Group ref={ref} wrap="nowrap">
          {children({ close: closePopover })}
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export default NavbarIconGroupItem;
